// Distributed tracing for the browser SDK (Sentry-parity).
//
// Captures spans that describe how a page load / interaction unfolds and groups
// them into a trace:
//   - a root `pageload` transaction (Navigation Timing),
//   - child spans for the navigation phases (request / response / dom),
//   - a `resource.<type>` child per Resource Timing entry (scripts, css, img,
//     fetch/xhr, fonts, …) so the trace reads as a granular waterfall,
//   - any custom spans the host app opens via `startSpan` / `startInactiveSpan`.
//
// All spans in a page load share a W3C-style `traceId`; children point at their
// `parentSpanId`. Outgoing same-origin requests get a `traceparent` /
// `sentry-trace` header so a backend that understands them can continue the
// trace (the "distributed" part). The module is self-contained and best-effort:
// any failure is swallowed so it can never break the host app.

import { logDebug, logWarn } from './utils/logger';
import { sanitizeUrl } from './redact';

const isBrowser = typeof window !== 'undefined';

export interface HBSpan {
    traceId: string;
    spanId: string;
    parentSpanId: string; // '' for a root span
    name: string;
    op: string;
    startTimeMs: number; // epoch ms
    durationMs: number;
    status: 'ok' | 'error' | 'cancelled';
    attributes?: Record<string, unknown>;
}

export interface TracingSessionContext {
    sessionId: string | null;
    endUserId: string | null;
    automaticProperties?: Record<string, unknown>;
    release?: string | null;
    environment?: string | null;
}

export interface TracingConfig {
    getSession: () => TracingSessionContext;
    // Batch flush. `useBeacon` is true on page unload (synchronous transport).
    sendSpans: (spans: HBSpan[], ctx: TracingSessionContext, useBeacon: boolean) => void;
    // Predicate for SDK-owned/ignored URLs (reuses the tracker's network skip).
    shouldSkipUrl?: (url: string) => boolean;
    // How long after `load` to collect resource spans before flushing the
    // page-load trace. Late resources are still captured by the observer.
    pageLoadFlushDelayMs?: number;
}

// Active, mutable span the host app is timing.
export interface InactiveSpan {
    setAttribute(key: string, value: unknown): void;
    setStatus(status: 'ok' | 'error' | 'cancelled'): void;
    end(): void;
}

const HEX = '0123456789abcdef';
function randHex(bytes: number): string {
    const len = bytes * 2;
    // Prefer crypto for unbiased ids; fall back to Math.random.
    try {
        const arr = new Uint8Array(bytes);
        (globalThis.crypto || (globalThis as any).msCrypto).getRandomValues(arr);
        let out = '';
        for (let i = 0; i < bytes; i++) out += arr[i].toString(16).padStart(2, '0');
        return out;
    } catch {
        let out = '';
        for (let i = 0; i < len; i++) out += HEX[(Math.random() * 16) | 0];
        return out;
    }
}

const newTraceId = () => randHex(16); // 32 hex chars
const newSpanId = () => randHex(8); //  16 hex chars

// Map a Resource Timing initiatorType to a span op.
function resourceOp(initiatorType: string): string {
    switch (initiatorType) {
        case 'fetch':
        case 'xmlhttprequest':
            return 'http.client';
        case 'script':
            return 'resource.script';
        case 'css':
        case 'link':
            return 'resource.css';
        case 'img':
        case 'image':
            return 'resource.img';
        case 'font':
            return 'resource.font';
        default:
            return initiatorType ? `resource.${initiatorType}` : 'resource.other';
    }
}

// Short, readable resource name: method-less path + filename.
function resourceName(url: string): string {
    try {
        const u = new URL(url, isBrowser ? window.location.href : undefined);
        const file = u.pathname.split('/').filter(Boolean).pop() || u.pathname || u.hostname;
        return file;
    } catch {
        return url.slice(0, 120);
    }
}

export class Tracing {
    private cfg: TracingConfig;
    private traceId: string = newTraceId();
    private rootSpanId: string = newSpanId();
    private buffer: HBSpan[] = [];
    private flushTimer: ReturnType<typeof setTimeout> | null = null;
    private resourceObserver: PerformanceObserver | null = null;
    private started = false;
    private originalFetch: typeof fetch | null = null;

    constructor(cfg: TracingConfig) {
        this.cfg = cfg;
    }

    start(): void {
        if (!isBrowser || this.started) return;
        this.started = true;
        try {
            this.installTracePropagation();
            this.observeResources();
            if (document.readyState === 'complete') {
                this.capturePageLoad();
            } else {
                window.addEventListener('load', () => this.capturePageLoad(), { once: true });
            }
        } catch (e) {
            logWarn('[SDK] tracing start failed:', e);
        }
    }

    /** Begin a fresh trace (e.g. an SPA route change). */
    startNewTrace(): void {
        this.traceId = newTraceId();
        this.rootSpanId = newSpanId();
    }

    /** W3C + Sentry trace-context headers for the current trace. */
    getTraceHeaders(): Record<string, string> {
        const childId = newSpanId();
        return {
            traceparent: `00-${this.traceId}-${childId}-01`,
            'sentry-trace': `${this.traceId}-${childId}-1`,
        };
    }

    /** Time a synchronous or async callback as a child span. */
    startSpan<T>(
        opts: { name: string; op?: string; attributes?: Record<string, unknown> },
        callback: () => T,
    ): T {
        const span = this.startInactiveSpan(opts);
        try {
            const result = callback();
            if (result instanceof Promise) {
                return result
                    .then((v) => {
                        span.end();
                        return v;
                    })
                    .catch((err) => {
                        span.setStatus('error');
                        span.end();
                        throw err;
                    }) as unknown as T;
            }
            span.end();
            return result;
        } catch (err) {
            span.setStatus('error');
            span.end();
            throw err;
        }
    }

    /** Open a span the caller ends manually. */
    startInactiveSpan(opts: {
        name: string;
        op?: string;
        attributes?: Record<string, unknown>;
    }): InactiveSpan {
        const startTimeMs = Date.now();
        const spanId = newSpanId();
        const traceId = this.traceId;
        const parentSpanId = this.rootSpanId;
        const attributes: Record<string, unknown> = { ...(opts.attributes || {}) };
        let status: 'ok' | 'error' | 'cancelled' = 'ok';
        let ended = false;
        const record = this.record.bind(this);
        return {
            setAttribute(key, value) {
                attributes[key] = value;
            },
            setStatus(next) {
                status = next;
            },
            end() {
                if (ended) return;
                ended = true;
                record({
                    traceId,
                    spanId,
                    parentSpanId,
                    name: opts.name,
                    op: opts.op || 'custom',
                    startTimeMs,
                    durationMs: Math.max(Date.now() - startTimeMs, 0),
                    status,
                    attributes,
                });
            },
        };
    }

    /** Flush buffered spans now. Called on unload (beacon) by the tracker. */
    flush(useBeacon = false): void {
        if (this.buffer.length === 0) return;
        const spans = this.buffer;
        this.buffer = [];
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }
        try {
            this.cfg.sendSpans(spans, this.cfg.getSession(), useBeacon);
        } catch (e) {
            logWarn('[SDK] tracing flush failed:', e);
        }
    }

    stop(): void {
        try {
            this.resourceObserver?.disconnect();
        } catch {
            /* ignore */
        }
        if (this.originalFetch && typeof window !== 'undefined') {
            window.fetch = this.originalFetch;
        }
        this.flush(false);
    }

    // ── internals ────────────────────────────────────────────────────────────

    private record(span: HBSpan): void {
        // Stamp release/environment onto every span so Monitoring env filters
        // can scope Performance without a separate join.
        try {
            const ctx = this.cfg.getSession();
            const attrs = { ...(span.attributes || {}) };
            if (ctx.release && attrs.release == null) attrs.release = ctx.release;
            if (ctx.environment && attrs.environment == null) {
                attrs.environment = ctx.environment;
            }
            span = { ...span, attributes: attrs };
        } catch {
            /* best-effort */
        }
        this.buffer.push(span);
        // Coalesce sends; cap buffer so a chatty page can't grow it unbounded.
        if (this.buffer.length >= 100) {
            this.flush(false);
            return;
        }
        if (!this.flushTimer) {
            this.flushTimer = setTimeout(() => this.flush(false), 4000);
        }
    }

    private capturePageLoad(): void {
        if (typeof performance === 'undefined') return;
        try {
            const nav = performance.getEntriesByType(
                'navigation',
            )[0] as PerformanceNavigationTiming | undefined;
            if (!nav) return;
            const origin = performance.timeOrigin;
            const startTimeMs = origin + nav.fetchStart;
            const endRel = nav.loadEventEnd || nav.domComplete || nav.responseEnd;
            const durationMs = Math.max(endRel - nav.fetchStart, 0);
            const pathname = isBrowser ? window.location.pathname : '/';

            // Root transaction.
            this.record({
                traceId: this.traceId,
                spanId: this.rootSpanId,
                parentSpanId: '',
                name: `pageload ${pathname}`,
                op: 'pageload',
                startTimeMs,
                durationMs,
                status: 'ok',
                attributes: {
                    'page.url': isBrowser ? sanitizeUrl(window.location.href) : '',
                    'page.route': pathname,
                    'http.response.transfer_size': nav.transferSize,
                    'navigation.type': nav.type,
                },
            });

            // Standard navigation-phase child spans (only emit non-zero ones).
            const phase = (
                name: string,
                op: string,
                startRel: number,
                endRelTime: number,
                attrs?: Record<string, unknown>,
            ) => {
                const dur = endRelTime - startRel;
                if (!(dur > 0) || !(startRel >= 0)) return;
                this.record({
                    traceId: this.traceId,
                    spanId: newSpanId(),
                    parentSpanId: this.rootSpanId,
                    name,
                    op,
                    startTimeMs: origin + startRel,
                    durationMs: dur,
                    status: 'ok',
                    attributes: attrs,
                });
            };
            phase('DNS lookup', 'browser.dns', nav.domainLookupStart, nav.domainLookupEnd);
            phase('TCP connect', 'browser.connect', nav.connectStart, nav.connectEnd);
            phase('Request', 'http.request', nav.requestStart, nav.responseStart, {
                'http.method': 'GET',
            });
            phase('Response', 'http.response', nav.responseStart, nav.responseEnd);
            phase(
                'DOM processing',
                'browser.dom',
                nav.domInteractive || nav.responseEnd,
                nav.domContentLoadedEventEnd || nav.domComplete,
            );
            phase('Resource load', 'browser.load', nav.domContentLoadedEventEnd, nav.loadEventEnd);

            // Resources already buffered before `load`.
            const resources = performance.getEntriesByType(
                'resource',
            ) as PerformanceResourceTiming[];
            for (const r of resources) this.recordResource(r, origin);

            // Flush the page-load trace shortly after, letting late resources land.
            const delay = this.cfg.pageLoadFlushDelayMs ?? 2500;
            setTimeout(() => this.flush(false), delay);
        } catch (e) {
            logWarn('[SDK] capturePageLoad failed:', e);
        }
    }

    private observeResources(): void {
        if (typeof PerformanceObserver === 'undefined') return;
        try {
            const origin = performance.timeOrigin;
            this.resourceObserver = new PerformanceObserver((list) => {
                for (const entry of list.getEntries()) {
                    this.recordResource(entry as PerformanceResourceTiming, origin);
                }
            });
            this.resourceObserver.observe({ type: 'resource', buffered: false });
        } catch (e) {
            logDebug('[SDK] resource observer unavailable:', e);
        }
    }

    private recordResource(r: PerformanceResourceTiming, origin: number): void {
        const url = r.name;
        if (!url) return;
        if (this.cfg.shouldSkipUrl && this.cfg.shouldSkipUrl(url)) return;
        const durationMs = Math.max(r.responseEnd - r.startTime, 0);
        if (!(durationMs >= 0)) return;
        this.record({
            traceId: this.traceId,
            spanId: newSpanId(),
            parentSpanId: this.rootSpanId,
            name: resourceName(url),
            op: resourceOp(r.initiatorType),
            startTimeMs: origin + r.startTime,
            durationMs,
            status: 'ok',
            attributes: {
                'resource.url': url.slice(0, 300),
                'resource.initiator': r.initiatorType,
                'resource.transfer_size': r.transferSize,
                'resource.encoded_size': r.encodedBodySize,
                'resource.decoded_size': r.decodedBodySize,
            },
        });
    }

    // Inject trace-context headers into same-origin fetch requests so a backend
    // can continue the trace. Best-effort: never blocks or alters the request
    // semantics, and chains to whatever `window.fetch` currently is (composes
    // with the network-error wrapper).
    private installTracePropagation(): void {
        if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
        this.originalFetch = window.fetch.bind(window);
        const self = this;
        window.fetch = function (
            input: RequestInfo | URL,
            init?: RequestInit,
        ): Promise<Response> {
            try {
                const url =
                    typeof input === 'string'
                        ? input
                        : input instanceof URL
                          ? input.toString()
                          : input.url;
                const sameOrigin = (() => {
                    try {
                        return new URL(url, window.location.href).origin === window.location.origin;
                    } catch {
                        return false;
                    }
                })();
                const skip = self.cfg.shouldSkipUrl ? self.cfg.shouldSkipUrl(url) : false;
                if (sameOrigin && !skip) {
                    const headers = new Headers(
                        init?.headers || (typeof input !== 'string' && !(input instanceof URL) ? input.headers : undefined),
                    );
                    const trace = self.getTraceHeaders();
                    if (!headers.has('traceparent')) headers.set('traceparent', trace.traceparent);
                    if (!headers.has('sentry-trace')) headers.set('sentry-trace', trace['sentry-trace']);
                    const nextInit: RequestInit = { ...(init || {}), headers };
                    return self.originalFetch!(input, nextInit);
                }
            } catch {
                /* fall through to the unmodified request */
            }
            return self.originalFetch!(input, init);
        } as typeof fetch;
    }
}
